import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload,
  SupabaseClient,
} from '@supabase/supabase-js';

import type { GpsPeer } from '../engine/gpsPeer';
import { clearRemoteGpsPeers, upsertRemoteGpsPeer } from '../engine/peerGpsStore';
import type { PeerProfilesModule } from './peerProfiles';

export type SubscriptionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'error';

export type SubscriptionStatusTransition = {
  from: SubscriptionStatus;
  to: SubscriptionStatus;
  reason: string;
};

type StatusListener = (status: SubscriptionStatus) => void;

interface PositionRow {
  user_id: string;
  lat: number;
  lng: number;
  accuracy_m: number | null;
  heading_deg: number | null;
  /** Epoch ms from the device. bigint, so PostgREST may hand it over as a string. */
  client_ts: number | string | null;
  server_ts: string;
}

export type PositionSubscriberOptions = {
  client: SupabaseClient;
  userId: string;
  peerProfiles?: PeerProfilesModule;
  onStatusTransition?: (transition: SubscriptionStatusTransition) => void;
  /**
   * The full user ids being watched, every time a read of lynx_event_peers lands. The engine files
   * every peer under one of these (one person, one entry).
   */
  onPeerIdsChange?: (peerIds: string[]) => void;
};

/**
 * What a session watches: the members of every group you share for the event, plus the people you follow back
 * who are going to it. `groupIds` is all of your groups for the session's event (Sam, 2026-10-12: nobody should
 * have to pick a group - two friends in different groups at the same show see each other); `groupId` is the
 * session's primary group, kept for the channel name.
 */
export type PositionSubscriptionTarget = {
  groupId: string | null;
  groupIds?: string[];
  eventId: string | null;
};

export interface PositionSubscriber {
  getStatus(): SubscriptionStatus;
  onStatusChange(listener: StatusListener): () => void;
  /** `knownPeerIds`: a list just read by the caller (refreshPeersIfChanged), used instead of reading it again. */
  start(target: PositionSubscriptionTarget, knownPeerIds?: string[]): Promise<void>;
  /** You joined or left a group for the event mid-session: watch the new set without restarting the session. */
  setGroupIds(groupIds: string[]): Promise<void>;
  /** Recover the channel after app resume without rebuilding the whole session. */
  resync(): Promise<void>;
  /**
   * Membership changed (a join approved here or on another phone): re-read who to watch now, not on
   * the 45 s timer, and retry names that were not readable yet (2026-10-14 Turnstile: 45 s to see a
   * friend approved 15 s after the request).
   */
  refreshPeers(): Promise<void>;
  /** The full user ids last read from lynx_event_peers (empty before the first read). */
  getPeerIds(): string[];
  stop(): Promise<void>;
}

export function rowToRemoteGpsPeer(row: PositionRow): GpsPeer {
  // Date the peer from when the fix was taken, not when the row was written. server_ts
  // says "we heard from this device recently", which is not the question the radar asks —
  // a heartbeat republishing a minute-old stationary fix would otherwise read as live.
  const clientTs = typeof row.client_ts === 'number' ? row.client_ts : Number(row.client_ts);
  const serverTs = new Date(row.server_ts).getTime();
  const lastKnownAt =
    Number.isFinite(clientTs) && clientTs > 0
      ? clientTs
      : Number.isFinite(serverTs) && serverTs > 0
        ? serverTs
        : Date.now();
  return {
    id: row.user_id,
    source: 'remote',
    lat: row.lat,
    lng: row.lng,
    accuracyMeters: row.accuracy_m,
    headingDegrees: row.heading_deg,
    lastKnownAt,
  };
}

/** How often a live subscription re-reads the group's member list (one small query). */
const PEER_LIST_REFRESH_MS = 45_000;

/** A failed position snapshot is read again after this, doubling to SNAPSHOT_RETRY_MAX_MS. */
export const SNAPSHOT_RETRY_MS = 5_000;
const SNAPSHOT_RETRY_MAX_MS = 60_000;

/**
 * The last good lynx_event_peers list, per user + event (2026-10-16 audit). A session that starts with no signal used
 * to leave the engine's roster unset, and an unset roster shows any Prii phone in Bluetooth range - strangers at a
 * festival. The last list read for this event is a far better answer than none. localStorage can throw (private
 * mode, quota) or be missing (tests), so every touch is guarded.
 */
const PEER_ROSTER_KEY_PREFIX = 'prii:find-roster:';

function rosterKey(userId: string, eventId: string | null): string | null {
  return userId && eventId ? `${PEER_ROSTER_KEY_PREFIX}${userId}:${eventId}` : null;
}

function rosterStore(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

export function readLastGoodPeerIds(userId: string, eventId: string | null): string[] | null {
  const key = rosterKey(userId, eventId);
  const store = rosterStore();
  if (!key || !store) return null;
  try {
    const parsed: unknown = JSON.parse(store.getItem(key) ?? 'null');
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string' && id !== '')
      : null;
  } catch {
    return null;
  }
}

export function writeLastGoodPeerIds(
  userId: string,
  eventId: string | null,
  peerIds: string[],
): void {
  const key = rosterKey(userId, eventId);
  const store = rosterStore();
  if (!key || !store) return;
  try {
    store.setItem(key, JSON.stringify(peerIds));
  } catch {
    // quota / private mode: the in-memory list still holds for this session
  }
}

export function createPositionSubscriber(options: PositionSubscriberOptions): PositionSubscriber {
  const { client, userId, peerProfiles, onStatusTransition, onPeerIdsChange } = options;

  let channel: RealtimeChannel | null = null;
  let activeTarget: PositionSubscriptionTarget | null = null;
  let activePeerIds: string[] = [];
  let currentStatus: SubscriptionStatus = 'idle';
  const statusListeners = new Set<StatusListener>();
  let bufferedEvents: RealtimePostgresChangesPayload<PositionRow>[] | null = null;
  let peerRefreshTimer: ReturnType<typeof setInterval> | null = null;
  let snapshotRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let snapshotRetryMs = SNAPSHOT_RETRY_MS;
  let peerRefreshInFlight = false;
  /** setGroupIds arrived mid-refresh: run once more when it lands (2026-10-12 audit). */
  let refreshAgain = false;
  /** setGroupIds before start() finished (the session was still starting): start() takes them. */
  let pendingGroupIds: string[] | null = null;
  /** Bumped by every start() and stop(): a start that outlives a stop must not subscribe a zombie channel. */
  let generation = 0;

  function setStatus(next: SubscriptionStatus, reason = 'unknown'): void {
    if (currentStatus === next) {
      return;
    }
    const from = currentStatus;
    currentStatus = next;
    onStatusTransition?.({ from, to: next, reason });
    statusListeners.forEach((cb) => cb(next));
  }

  function handleRealtimeEvent(payload: RealtimePostgresChangesPayload<PositionRow>): void {
    const row = payload.new as PositionRow | undefined;
    if (!row?.user_id || row.user_id === userId) {
      return;
    }
    upsertRemoteGpsPeer(rowToRemoteGpsPeer(row));
    void peerProfiles?.fetchProfileFor(row.user_id);
  }

  /**
   * Who to watch.
   *
   * Positions stopped being group-scoped in migration 121 — a device is in one place
   * however many groups reference it — so the target is resolved to a list of people and
   * the query filters on them. Since 141 that list is lynx_event_peers(): the group's active
   * members (if there is a group) plus the people you follow back who are going to the same
   * event (Sam, 2026-10-04: friends should not need a group). RLS still decides what actually
   * comes back; this only narrows the request.
   *
   * Re-read on every start(), on every resync(), and on a slow timer while subscribed: the
   * channel filter is fixed at subscribe time, so someone approved into the group after the
   * session began was invisible until the whole session was rebuilt (2026-10-03, Pershing
   * Square - the creator never saw the member she approved). A changed list rebuilds the
   * channel for the same target.
   */
  async function fetchPeerIds(target: PositionSubscriptionTarget): Promise<string[] | null> {
    const groups = target.groupIds?.length ? target.groupIds : [target.groupId];
    if (!groups.some(Boolean) && !target.eventId) {
      return [];
    }
    // One read per group: lynx_event_peers takes one group, and every read also returns the mutual follows going
    // to the event, so a session with no group still makes one (null) read.
    const reads = await Promise.all(
      groups.map((groupId) =>
        client.rpc('lynx_event_peers', { p_event_id: target.eventId, p_group_id: groupId ?? null }),
      ),
    );
    if (reads.some((r) => r.error)) {
      return null; // a failed read during a signal gap is not "nobody" (2026-10-04 audit)
    }
    const ids: string[] = [];
    for (const { data } of reads) {
      for (const row of (data ?? []) as unknown[]) {
        const id =
          typeof row === 'string'
            ? row
            : (row as { lynx_event_peers?: string } | null)?.lynx_event_peers;
        if (typeof id === 'string' && id && id !== userId) {
          ids.push(id);
        }
      }
    }
    return [...new Set(ids)];
  }

  /** False when the read failed (a retry is then scheduled). */
  async function fetchAndApplySnapshot(): Promise<boolean> {
    if (!activeTarget || activePeerIds.length === 0) {
      return true;
    }

    const { data, error } = await client
      .from('lynx_user_positions')
      .select('user_id, lat, lng, accuracy_m, heading_deg, client_ts, server_ts')
      .in('user_id', activePeerIds);

    if (error) {
      setStatus('error', `snapshot_fetch:${error.code ?? error.message ?? 'unknown'}`);
      // The channel works even when this read does not. Returning with the buffer still open held every realtime
      // fix in it for good: Find showed nobody, with every friend's position arriving, until the channel was rebuilt
      // (2026-11-01 sim big-group, a snapshot timed out on a slow start). Show them now - upsertRemoteGpsPeer keeps
      // the newer fix, so the snapshot no longer needs to go first - and read again shortly for anyone standing still.
      drainBufferedEvents();
      scheduleSnapshotRetry();
      return false;
    }
    snapshotRetryMs = SNAPSHOT_RETRY_MS;

    const rows = data ?? [];
    for (const row of rows) {
      if (row.user_id === userId) {
        continue;
      }
      upsertRemoteGpsPeer(rowToRemoteGpsPeer(row as PositionRow));
    }

    const uniqueUserIds = [
      ...new Set(rows.map((row) => row.user_id).filter((id) => id !== userId)),
    ];
    if (uniqueUserIds.length > 0) {
      await peerProfiles?.fetchProfilesFor(uniqueUserIds);
    }

    drainBufferedEvents();
    return true;
  }

  function drainBufferedEvents(): void {
    const buffered = bufferedEvents ?? [];
    bufferedEvents = null;
    for (const evt of buffered) {
      handleRealtimeEvent(evt);
    }
  }

  function stopSnapshotRetry(): void {
    if (snapshotRetryTimer !== null) {
      clearTimeout(snapshotRetryTimer);
      snapshotRetryTimer = null;
    }
  }

  function scheduleSnapshotRetry(): void {
    stopSnapshotRetry();
    const myGeneration = generation;
    const delay = snapshotRetryMs;
    snapshotRetryMs = Math.min(snapshotRetryMs * 2, SNAPSHOT_RETRY_MAX_MS);
    snapshotRetryTimer = setTimeout(() => {
      snapshotRetryTimer = null;
      if (generation !== myGeneration || channel === null) return;
      void fetchAndApplySnapshot().then((ok) => {
        if (ok && generation === myGeneration && channel !== null) {
          setStatus('connected', 'snapshot_retry');
        }
      });
    }, delay);
  }

  function sameMembers(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((id) => set.has(id));
  }

  function stopPeerRefresh(): void {
    if (peerRefreshTimer !== null) {
      clearInterval(peerRefreshTimer);
      peerRefreshTimer = null;
    }
  }

  /** Someone joined, left or followed back since subscribe: rebuild the channel with the current list. */
  async function refreshPeersIfChanged(): Promise<boolean> {
    const target = activeTarget;
    if (peerRefreshInFlight) {
      refreshAgain = true;
      return false;
    }
    if (target === null || channel === null) {
      return false;
    }
    peerRefreshInFlight = true;
    const myGeneration = generation;
    try {
      const latest = await fetchPeerIds(target);
      if (
        latest === null ||
        activeTarget !== target ||
        channel === null ||
        sameMembers(latest, activePeerIds)
      ) {
        return false;
      }
      await client.removeChannel(channel);
      channel = null;
      // stop() ran during the await: do not bring a stopped subscriber back to life (a zombie channel + timer).
      if (generation !== myGeneration) {
        return false;
      }
      activeTarget = null;
      stopPeerRefresh();
      void peerProfiles?.retryMissingProfiles();
      // Hand over the list just read: start() reading it again could fail in a signal gap and rebuild the channel
      // with the old filter, the change this refresh found lost until the next timer (2026-10-16 audit).
      await subscriber.start(target, latest);
      return true;
    } finally {
      peerRefreshInFlight = false;
      if (refreshAgain) {
        refreshAgain = false;
        void refreshPeersIfChanged();
      }
    }
  }

  const subscriber: PositionSubscriber = {
    getStatus() {
      return currentStatus;
    },

    onStatusChange(listener) {
      statusListeners.add(listener);
      return () => {
        statusListeners.delete(listener);
      };
    },

    async setGroupIds(groupIds) {
      if (activeTarget === null) {
        pendingGroupIds = groupIds;
        return;
      }
      // In place: refreshPeersIfChanged() holds the same object and rebuilds the channel if the people changed.
      activeTarget.groupIds = groupIds;
      await refreshPeersIfChanged();
    },

    async start(target, knownPeerIds) {
      if (channel !== null) {
        return;
      }

      const myGeneration = ++generation;
      stopSnapshotRetry();
      snapshotRetryMs = SNAPSHOT_RETRY_MS;
      if (pendingGroupIds) {
        target.groupIds = pendingGroupIds;
        pendingGroupIds = null;
      }
      activeTarget = target;
      setStatus('connecting', 'start');
      bufferedEvents = [];
      // A failed read keeps the last list: a signal gap is not "nobody" (2026-10-04 audit).
      const readPeerIds = knownPeerIds ? [...knownPeerIds] : await fetchPeerIds(target);
      if (generation !== myGeneration || activeTarget !== target) {
        return; // stop() or another start() ran while the peer list was being read
      }
      if (readPeerIds !== null) {
        activePeerIds = readPeerIds;
        writeLastGoodPeerIds(userId, target.eventId, activePeerIds);
        onPeerIdsChange?.([...activePeerIds]);
      } else if (activePeerIds.length === 0) {
        // First read of the session failed (started offline): use the list last read for this event rather than
        // leave the roster unset, which shows strangers (2026-10-16 audit).
        const lastGood = readLastGoodPeerIds(userId, target.eventId);
        if (lastGood) {
          activePeerIds = lastGood;
          onPeerIdsChange?.([...activePeerIds]);
        }
      }
      // Names for everyone on the roster up front, not per position row: a friend whose first fix
      // came over Bluetooth never got a fetch and stayed "Group member" (2026-10-14 Turnstile).
      if (activePeerIds.length > 0) {
        void peerProfiles?.fetchProfilesFor(activePeerIds);
      }

      // An empty `in.()` is not a valid filter, and a group of one has nobody to watch.
      // Subscribing to a uuid that cannot exist keeps the channel and its status
      // transitions identical to the populated case, so resync() still works when someone
      // joins later.
      const peerFilter =
        activePeerIds.length > 0 ? activePeerIds.join(',') : '00000000-0000-0000-0000-000000000000';

      // Every join, approve, auto-match, leave and removal in your groups for this event, from any phone or path
      // (migration 154 puts group_members on realtime): re-read the roster now. Before, a plain member heard nothing -
      // member_joined only reaches managers - and waited out the 45 s timer while the newcomer's fixes were dropped
      // as strangers (2026-10-16 audit).
      const watchedGroupIds = target.groupIds?.length
        ? target.groupIds
        : target.groupId
          ? [target.groupId]
          : [];

      let built = client
        .channel(`lynx-positions-${target.groupId ?? 'solo'}-${target.eventId ?? ''}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'lynx_user_positions',
            filter: `user_id=in.(${peerFilter})`,
          },
          (payload) => {
            if (bufferedEvents !== null) {
              bufferedEvents.push(payload as RealtimePostgresChangesPayload<PositionRow>);
              return;
            }
            handleRealtimeEvent(payload as RealtimePostgresChangesPayload<PositionRow>);
          },
        );
      if (watchedGroupIds.length > 0) {
        built = built.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'group_members',
            filter: `group_id=in.(${watchedGroupIds.join(',')})`,
          },
          () => {
            void refreshPeersIfChanged();
          },
        );
      }
      channel = built;
      // Join once the socket holds the session's current token: a join sent before it has one goes out as anon, and one
      // sent on a token expired in a pocket stays on it; either way RLS drops every position without an error
      // (whenRealtimeAuthed, src/lib/realtime/subscriptionHealth.js).
      const socket = client.realtime as typeof client.realtime | undefined; // undefined in test doubles
      if (socket?.setAuth) {
        await socket.setAuth().catch(() => {});
        // Replaced or stopped while the token was on its way.
        if (channel !== built || activeTarget !== target) return;
      }
      built.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          void fetchAndApplySnapshot().then(() => {
            if (activeTarget === target && channel !== null) {
              setStatus('connected', 'realtime_subscribed');
              // The list was read before this channel joined, and nothing heard group_members in between: a join or
              // leave in that gap waited out the 45 s timer. Three changes a second apart did exactly that - the
              // third landed while every phone was rebuilding for the first two (2026-11-01 sim big-group). Read it
              // once more now; unchanged, this is one small query and stops here.
              void refreshPeersIfChanged();
            }
          });
        } else if (status === 'CHANNEL_ERROR') {
          setStatus('error', 'channel_error');
        } else if (status === 'TIMED_OUT') {
          setStatus('error', 'timed_out');
        } else if (status === 'CLOSED') {
          setStatus('reconnecting', 'channel_closed');
        }
      });

      stopPeerRefresh();
      peerRefreshTimer = setInterval(() => {
        // Hidden: no query every 45 s all night; resync() re-reads on resume (an idle web view, 2026-10-29).
        if (typeof document !== 'undefined' && document.hidden) return;
        void refreshPeersIfChanged();
      }, PEER_LIST_REFRESH_MS);
    },

    /**
     * Re-establish after a background stretch. A websocket rarely survives iOS
     * suspending the app, and supabase-js does not always rejoin on its own — at Red
     * Rocks one device sat in error/reconnecting until the whole session was rebuilt.
     * Healthy channel: just re-fetch the snapshot to catch updates missed while away.
     * Unhealthy: drop it and re-subscribe for the same group.
     */
    async resync() {
      const target = activeTarget;
      if (target === null) {
        return;
      }

      if (channel !== null && currentStatus === 'connected') {
        if (await refreshPeersIfChanged()) {
          return;
        }
        await fetchAndApplySnapshot();
        return;
      }

      if (channel !== null) {
        await client.removeChannel(channel);
        channel = null;
      }
      activeTarget = null;
      await this.start(target);
    },

    async refreshPeers() {
      void peerProfiles?.retryMissingProfiles();
      await refreshPeersIfChanged();
    },

    getPeerIds() {
      return [...activePeerIds];
    },

    async stop() {
      generation += 1;
      pendingGroupIds = null;
      refreshAgain = false;
      stopPeerRefresh();
      stopSnapshotRetry();
      activePeerIds = [];
      if (channel !== null) {
        await client.removeChannel(channel);
        channel = null;
      }
      clearRemoteGpsPeers();
      peerProfiles?.clearProfiles();
      bufferedEvents = null;
      activeTarget = null;
      setStatus('idle', 'stop');
    },
  };

  return subscriber;
}
